// the following code is mostly copied (and adapted to typescript) from the
import {
  Vector2,
  PerspectiveCamera,
  OrthographicCamera,
  Scene,
  Object3D,
  Frustum,
  Vector3,
  BatchedMesh,
  EdgesGeometry,
  Mesh,
  Plane,
  Box3,
  Matrix4,
  BufferAttribute,
} from 'three'
import { objectGeometryToWorldVertices } from '@modugen/ui-toolkit'

interface SelectionBounds2D {
  minX: number
  maxX: number
  minY: number
  maxY: number
}

// ---------------------------------------------------------------------------
// Drag-scoped broad-phase cache
//
// The geometry and world transforms of the scene do not change while the user
// drags a marquee rectangle - only the selection frustum changes from sample to
// sample. We therefore walk the scene once at the start of a drag, precompute a
// flat list of selectable entries together with their world-space bounding
// boxes, and reuse that list for every subsequent sample. Each sample then only
// runs cheap AABB tests (and the expensive per-vertex test for the handful of
// boundary objects that actually straddle the selection).
// ---------------------------------------------------------------------------

interface MeshEntry {
  object: Mesh
  name: string
  worldAABB: Box3
  clipPlanes: Plane[]
}

interface InstanceEntry {
  batchedMesh: BatchedMesh
  instanceId: number
  id: string
  worldAABB: Box3
  clipPlanes: Plane[]
}

export interface MarqueeSelectionCache {
  built: boolean
  meshes: MeshEntry[]
  instances: InstanceEntry[]
}

export function createMarqueeSelectionCache(): MarqueeSelectionCache {
  return { built: false, meshes: [], instances: [] }
}

export function resetMarqueeSelectionCache(cache: MarqueeSelectionCache): void {
  cache.built = false
  cache.meshes.length = 0
  cache.instances.length = 0
}

// Reusable scratch objects to keep the hot path allocation-free.
const _box = new Box3()
const _localBox = new Box3()
const _instanceMatrix = new Matrix4()
const _corner = new Vector3()
const _vertex = new Vector3()
const _rangeTarget = {
  vertexStart: 0,
  vertexCount: 0,
  reservedVertexCount: 0,
  indexStart: 0,
  indexCount: 0,
  reservedIndexCount: 0,
  start: 0,
  count: 0,
}

function resolveClipPlanesForObject(object: Object3D, globalClipPlanes: Plane[]): Plane[] {
  if (!(object instanceof Mesh) && !(object instanceof BatchedMesh)) return globalClipPlanes

  const material = object.material
  if (!material) return globalClipPlanes

  const materialClippingPlanes = Array.isArray(material)
    ? material.flatMap(m => m.clippingPlanes ?? [])
    : material.clippingPlanes ?? []

  return materialClippingPlanes.length > 0 ? materialClippingPlanes : globalClipPlanes
}

function hasAnyPointInsideClipPlanes(points: { v: Vector3 }[], clipPlanes: Plane[]): boolean {
  if (clipPlanes.length === 0) return true

  for (const point of points) {
    let insideAll = true
    for (const plane of clipPlanes) {
      if (plane.distanceToPoint(point.v) < 0) {
        insideAll = false
        break
      }
    }
    if (insideAll) return true
  }

  return false
}

// ---------------------------------------------------------------------------
// World-space AABB computation
// ---------------------------------------------------------------------------

function computeMeshWorldAABB(mesh: Mesh, target: Box3): boolean {
  const geometry = mesh.geometry
  if (!geometry) return false
  if (!geometry.boundingBox) geometry.computeBoundingBox()
  if (!geometry.boundingBox) return false

  target.copy(geometry.boundingBox).applyMatrix4(mesh.matrixWorld)
  return true
}

function computeInstanceWorldAABB(
  batchedMesh: BatchedMesh,
  instanceId: number,
  target: Box3,
): boolean {
  const geometryId = batchedMesh.getGeometryIdAt(instanceId)
  if (geometryId < 0) return false

  // getBoundingBoxAt returns the geometry bounds in the BatchedMesh's local
  // space; combine it with the instance matrix and the mesh world matrix to get
  // a world-space AABB. getBoundingBoxAt caches the per-geometry box internally,
  // so this stays cheap across instances and samples.
  const localBox = batchedMesh.getBoundingBoxAt(geometryId, _localBox)
  if (localBox === null) return false

  batchedMesh.getMatrixAt(instanceId, _instanceMatrix)
  _instanceMatrix.premultiply(batchedMesh.matrixWorld)
  target.copy(_localBox).applyMatrix4(_instanceMatrix)
  return true
}

// ---------------------------------------------------------------------------
// Allocation-free broad-phase tests
// ---------------------------------------------------------------------------

// Project the 8 corners of a world-space AABB to NDC and test whether the
// resulting screen-space bounds overlap the selection rectangle. This is a
// conservative superset of the projected geometry, which matches the
// approximate nature of "touch" selection.
function aabbIntersectsSelection2D(
  aabb: Box3,
  camera: PerspectiveCamera | OrthographicCamera,
  selectionBounds2D: SelectionBounds2D,
): boolean {
  const { min, max } = aabb
  let minX = Number.POSITIVE_INFINITY
  let maxX = Number.NEGATIVE_INFINITY
  let minY = Number.POSITIVE_INFINITY
  let maxY = Number.NEGATIVE_INFINITY

  for (let xi = 0; xi < 2; xi++) {
    for (let yi = 0; yi < 2; yi++) {
      for (let zi = 0; zi < 2; zi++) {
        _corner.set(xi === 0 ? min.x : max.x, yi === 0 ? min.y : max.y, zi === 0 ? min.z : max.z)
        _corner.project(camera)
        if (_corner.x < minX) minX = _corner.x
        if (_corner.x > maxX) maxX = _corner.x
        if (_corner.y < minY) minY = _corner.y
        if (_corner.y > maxY) maxY = _corner.y
      }
    }
  }

  return !(
    maxX < selectionBounds2D.minX ||
    minX > selectionBounds2D.maxX ||
    maxY < selectionBounds2D.minY ||
    minY > selectionBounds2D.maxY
  )
}

// True only when every corner of the AABB is inside the frustum. Because the
// AABB contains all of the object's vertices, a fully-enclosed box implies all
// vertices are enclosed, so we can accept without iterating vertices.
function aabbFullyInsideFrustum(aabb: Box3, frustum: Frustum): boolean {
  const { min, max } = aabb
  for (let xi = 0; xi < 2; xi++) {
    for (let yi = 0; yi < 2; yi++) {
      for (let zi = 0; zi < 2; zi++) {
        _corner.set(xi === 0 ? min.x : max.x, yi === 0 ? min.y : max.y, zi === 0 ? min.z : max.z)
        if (!frustum.containsPoint(_corner)) return false
      }
    }
  }
  return true
}

function allPointsInsideFrustum(points: { v: Vector3 }[], frustum: Frustum): boolean {
  for (const p of points) {
    if (!frustum.containsPoint(p.v)) return false
  }
  return true
}

function getPositionAttribute(object: Mesh | BatchedMesh): BufferAttribute | undefined {
  const attribute = object.geometry?.attributes?.position
  if (!attribute || 'isGLBufferAttribute' in attribute) return undefined
  return attribute as BufferAttribute
}

// Allocation-free, early-exiting "are all vertices inside the frustum?" test for
// a plain mesh. Used only for boundary objects whose AABB straddles the frustum.
function meshAllVerticesInsideFrustum(mesh: Mesh, frustum: Frustum): boolean {
  const position = getPositionAttribute(mesh)
  if (!position) return false

  const matrixWorld = mesh.matrixWorld
  for (let i = 0; i < position.count; i++) {
    _vertex.fromBufferAttribute(position, i).applyMatrix4(matrixWorld)
    if (!frustum.containsPoint(_vertex)) return false
  }
  return true
}

// Allocation-free counterpart for a single BatchedMesh instance.
function instanceAllVerticesInsideFrustum(
  batchedMesh: BatchedMesh,
  instanceId: number,
  frustum: Frustum,
): boolean {
  const position = getPositionAttribute(batchedMesh)
  if (!position) return false

  const range = batchedMesh.getGeometryRangeAt(
    batchedMesh.getGeometryIdAt(instanceId),
    _rangeTarget,
  )
  if (range === null) return false

  batchedMesh.getMatrixAt(instanceId, _instanceMatrix)
  _instanceMatrix.premultiply(batchedMesh.matrixWorld)

  const { vertexStart, vertexCount } = range
  for (let v = 0; v < vertexCount; v++) {
    _vertex.fromBufferAttribute(position, vertexStart + v).applyMatrix4(_instanceMatrix)
    if (!frustum.containsPoint(_vertex)) return false
  }
  return true
}

// ---------------------------------------------------------------------------
// Cache building
// ---------------------------------------------------------------------------

function addBatchedEntries(
  batchedMesh: BatchedMesh,
  globalClipPlanes: Plane[],
  cache: MarqueeSelectionCache,
): void {
  const instanceIdToId = batchedMesh.userData.instanceIdToId as Record<number, string> | undefined
  if (instanceIdToId === undefined) return

  const childIdToGroupIFCId = batchedMesh.userData.childIdToGroupIFCId as
    | Record<string, string>
    | undefined

  const clipPlanes = resolveClipPlanesForObject(batchedMesh, globalClipPlanes)
  const instanceIds = Object.keys(instanceIdToId)

  for (let i = 0; i < instanceIds.length; i++) {
    const instanceId = Number(instanceIds[i])

    if (!batchedMesh.getVisibleAt(instanceId)) continue

    const guid = instanceIdToId[instanceId]
    if (guid === undefined) continue

    // If the instance is a child of a group, use the group IFC id instead of
    // the instance id (e.g. for openings or other objects).
    const usedGuid = childIdToGroupIFCId?.[guid] ?? guid

    if (!computeInstanceWorldAABB(batchedMesh, instanceId, _box)) continue

    cache.instances.push({
      batchedMesh,
      instanceId,
      id: usedGuid,
      worldAABB: _box.clone(),
      clipPlanes,
    })
  }
}

function collectCacheEntries(
  object: Object3D,
  camera: PerspectiveCamera | OrthographicCamera,
  globalClipPlanes: Plane[],
  cache: MarqueeSelectionCache,
  parentVisibleInCamera: boolean,
): void {
  const isVisibleInCamera =
    parentVisibleInCamera && object.visible && object.layers.test(camera.layers)

  if (!isVisibleInCamera) return

  if (
    object instanceof BatchedMesh &&
    object.material &&
    !(object.geometry instanceof EdgesGeometry)
  ) {
    addBatchedEntries(object, globalClipPlanes, cache)
  } else if (
    object instanceof Mesh &&
    object.material &&
    !(object.geometry instanceof EdgesGeometry) &&
    typeof object.name === 'string' &&
    object.name.length > 0
  ) {
    if (computeMeshWorldAABB(object, _box)) {
      cache.meshes.push({
        object,
        name: object.name,
        worldAABB: _box.clone(),
        clipPlanes: resolveClipPlanesForObject(object, globalClipPlanes),
      })
    }
  }

  for (let i = 0; i < object.children.length; i++) {
    collectCacheEntries(object.children[i], camera, globalClipPlanes, cache, isVisibleInCamera)
  }
}

function buildCache(
  cache: MarqueeSelectionCache,
  scene: Scene,
  camera: PerspectiveCamera | OrthographicCamera,
  globalClipPlanes: Plane[],
): void {
  resetMarqueeSelectionCache(cache)
  scene.updateMatrixWorld()
  collectCacheEntries(scene, camera, globalClipPlanes, cache, true)
  cache.built = true
}

// ---------------------------------------------------------------------------
// Per-sample evaluation
// ---------------------------------------------------------------------------

function selectMeshEntry(
  entry: MeshEntry,
  frustum: Frustum,
  camera: PerspectiveCamera | OrthographicCamera,
  mode: 'touch' | 'full',
  selectionBounds2D: SelectionBounds2D,
  objects: Object3D[],
  ids: Set<string>,
): void {
  const aabb = entry.worldAABB

  if (mode === 'touch') {
    if (!aabbIntersectsSelection2D(aabb, camera, selectionBounds2D)) return

    if (entry.clipPlanes.length === 0) {
      objects.push(entry.object)
      ids.add(entry.name)
      return
    }

    // Clip planes present: refine against the actual geometry.
    const points = objectGeometryToWorldVertices(entry.object)
    if (!hasAnyPointInsideClipPlanes(points, entry.clipPlanes)) return
    if (projectedBoundsIntersectSelection(points, camera, selectionBounds2D)) {
      objects.push(entry.object)
      ids.add(entry.name)
    }
    return
  }

  // full mode
  if (!frustum.intersectsBox(aabb)) return

  if (entry.clipPlanes.length === 0) {
    if (
      aabbFullyInsideFrustum(aabb, frustum) ||
      meshAllVerticesInsideFrustum(entry.object, frustum)
    ) {
      objects.push(entry.object)
      ids.add(entry.name)
    }
    return
  }

  // Clip planes present: refine against the actual geometry.
  const points = objectGeometryToWorldVertices(entry.object)
  if (!hasAnyPointInsideClipPlanes(points, entry.clipPlanes)) return
  if (allPointsInsideFrustum(points, frustum)) {
    objects.push(entry.object)
    ids.add(entry.name)
  }
}

function selectInstanceEntry(
  entry: InstanceEntry,
  frustum: Frustum,
  camera: PerspectiveCamera | OrthographicCamera,
  mode: 'touch' | 'full',
  selectionBounds2D: SelectionBounds2D,
  objects: Object3D[],
  ids: Set<string>,
): void {
  const aabb = entry.worldAABB

  if (mode === 'touch') {
    if (!aabbIntersectsSelection2D(aabb, camera, selectionBounds2D)) return

    if (entry.clipPlanes.length === 0) {
      objects.push(entry.batchedMesh)
      ids.add(entry.id)
      return
    }

    const points = objectGeometryToWorldVertices(entry.batchedMesh, entry.instanceId)
    if (points.length === 0) return
    if (!hasAnyPointInsideClipPlanes(points, entry.clipPlanes)) return
    if (projectedBoundsIntersectSelection(points, camera, selectionBounds2D)) {
      objects.push(entry.batchedMesh)
      ids.add(entry.id)
    }
    return
  }

  // full mode
  if (!frustum.intersectsBox(aabb)) return

  if (entry.clipPlanes.length === 0) {
    if (
      aabbFullyInsideFrustum(aabb, frustum) ||
      instanceAllVerticesInsideFrustum(entry.batchedMesh, entry.instanceId, frustum)
    ) {
      objects.push(entry.batchedMesh)
      ids.add(entry.id)
    }
    return
  }

  const points = objectGeometryToWorldVertices(entry.batchedMesh, entry.instanceId)
  if (points.length === 0) return
  if (!hasAnyPointInsideClipPlanes(points, entry.clipPlanes)) return
  if (allPointsInsideFrustum(points, frustum)) {
    objects.push(entry.batchedMesh)
    ids.add(entry.id)
  }
}

function evaluateCache(
  cache: MarqueeSelectionCache,
  frustum: Frustum,
  camera: PerspectiveCamera | OrthographicCamera,
  mode: 'touch' | 'full',
  selectionBounds2D: SelectionBounds2D,
): { objects: Object3D[]; ids: Set<string> } {
  const objects: Object3D[] = []
  const ids = new Set<string>()

  for (let i = 0; i < cache.meshes.length; i++) {
    selectMeshEntry(cache.meshes[i], frustum, camera, mode, selectionBounds2D, objects, ids)
  }
  for (let i = 0; i < cache.instances.length; i++) {
    selectInstanceEntry(cache.instances[i], frustum, camera, mode, selectionBounds2D, objects, ids)
  }

  return { objects, ids }
}

function projectedBoundsIntersectSelection(
  points: { v: Vector3 }[],
  camera: PerspectiveCamera | OrthographicCamera,
  selectionBounds2D: SelectionBounds2D,
): boolean {
  if (points.length === 0) return false

  const projectedPoint = new Vector3()
  let minX = Number.POSITIVE_INFINITY
  let maxX = Number.NEGATIVE_INFINITY
  let minY = Number.POSITIVE_INFINITY
  let maxY = Number.NEGATIVE_INFINITY

  for (const p of points) {
    projectedPoint.copy(p.v).project(camera)
    minX = Math.min(minX, projectedPoint.x)
    maxX = Math.max(maxX, projectedPoint.x)
    minY = Math.min(minY, projectedPoint.y)
    maxY = Math.max(maxY, projectedPoint.y)
  }

  return !(
    maxX < selectionBounds2D.minX ||
    minX > selectionBounds2D.maxX ||
    maxY < selectionBounds2D.minY ||
    minY > selectionBounds2D.maxY
  )
}

// three js selection box, see here: https://bit.ly/3GjGW81
export function marqueeObjectSelection(
  startPoint: Vector2,
  endPoint: Vector2,
  camera: PerspectiveCamera | OrthographicCamera,
  scene: Scene,
  mode: 'full' | 'touch',
  globalClipPlanes: Plane[] = [],
  cache?: MarqueeSelectionCache,
): { objectsInFrustum: Object3D[]; idsInFrustum: string[] } {
  const selectionBounds2D: SelectionBounds2D = {
    minX: Math.min(startPoint.x, endPoint.x),
    maxX: Math.max(startPoint.x, endPoint.x),
    minY: Math.min(startPoint.y, endPoint.y),
    maxY: Math.max(startPoint.y, endPoint.y),
  }

  // avoid invalid frustum
  if (startPoint.x === endPoint.x) endPoint.x += Number.EPSILON
  if (startPoint.y === endPoint.y) endPoint.y += Number.EPSILON

  camera.updateProjectionMatrix()
  camera.updateMatrixWorld()

  const frustum = new Frustum()
  // Use a finite distance for the far plane. Number.MAX_VALUE makes the three
  // far points nearly collinear (same direction from camera), so the cross
  // product in setFromCoplanarPoints becomes ~0 and the plane normal is NaN/Inf.
  const deep = camera instanceof PerspectiveCamera ? Math.min(camera.far, 1e10) : Number.MAX_VALUE

  if (camera instanceof PerspectiveCamera) {
    const tmpPoint = new Vector3()
    tmpPoint.x = Math.min(startPoint.x, endPoint.x)
    tmpPoint.y = Math.max(startPoint.y, endPoint.y)

    endPoint.x = Math.max(startPoint.x, endPoint.x)
    endPoint.y = Math.min(startPoint.y, endPoint.y)

    const vecNear = new Vector3()
    const vecTopLeft = new Vector3()
    const vecTopRight = new Vector3()
    const vecDownRight = new Vector3()
    const vecDownLeft = new Vector3()

    vecNear.setFromMatrixPosition(camera.matrixWorld)
    vecTopLeft.copy(tmpPoint)
    vecTopRight.set(endPoint.x, tmpPoint.y, 0)
    vecDownRight.copy(new Vector3(endPoint.x, endPoint.y, 0))
    vecDownLeft.set(tmpPoint.x, endPoint.y, 0)

    vecTopLeft.unproject(camera)
    vecTopRight.unproject(camera)
    vecDownRight.unproject(camera)
    vecDownLeft.unproject(camera)

    const vectemp1 = new Vector3()
    const vectemp2 = new Vector3()
    const vectemp3 = new Vector3()

    vectemp1.copy(vecTopLeft).sub(vecNear)
    vectemp2.copy(vecTopRight).sub(vecNear)
    vectemp3.copy(vecDownRight).sub(vecNear)
    vectemp1.normalize()
    vectemp2.normalize()
    vectemp3.normalize()

    vectemp1.multiplyScalar(deep)
    vectemp2.multiplyScalar(deep)
    vectemp3.multiplyScalar(deep)
    vectemp1.add(vecNear)
    vectemp2.add(vecNear)
    vectemp3.add(vecNear)

    const planes = frustum.planes

    planes[0].setFromCoplanarPoints(vecNear, vecTopLeft, vecTopRight)
    planes[1].setFromCoplanarPoints(vecNear, vecTopRight, vecDownRight)
    planes[2].setFromCoplanarPoints(vecDownRight, vecDownLeft, vecNear)
    planes[3].setFromCoplanarPoints(vecDownLeft, vecTopLeft, vecNear)
    planes[4].setFromCoplanarPoints(vecTopRight, vecDownRight, vecDownLeft)
    planes[5].setFromCoplanarPoints(vectemp3, vectemp2, vectemp1)
    planes[5].normal.multiplyScalar(-1)
  } else if (camera instanceof OrthographicCamera) {
    const left = Math.min(startPoint.x, endPoint.x)
    const top = Math.max(startPoint.y, endPoint.y)
    const right = Math.max(startPoint.x, endPoint.x)
    const down = Math.min(startPoint.y, endPoint.y)

    const vecTopLeft = new Vector3()
    const vecTopRight = new Vector3()
    const vecDownRight = new Vector3()
    const vecDownLeft = new Vector3()

    const vecFarTopLeft = new Vector3()
    const vecFarTopRight = new Vector3()
    const vecFarDownRight = new Vector3()
    const vecFarDownLeft = new Vector3()

    vecTopLeft.set(left, top, -1)
    vecTopRight.set(right, top, -1)
    vecDownRight.set(right, down, -1)
    vecDownLeft.set(left, down, -1)
    vecFarTopLeft.set(left, top, 1)
    vecFarTopRight.set(right, top, 1)
    vecFarDownRight.set(right, down, 1)
    vecFarDownLeft.set(left, down, 1)
    vecTopLeft.unproject(camera)
    vecTopRight.unproject(camera)
    vecDownRight.unproject(camera)
    vecDownLeft.unproject(camera)
    vecFarTopLeft.unproject(camera)
    vecFarTopRight.unproject(camera)
    vecFarDownRight.unproject(camera)
    vecFarDownLeft.unproject(camera)

    const planes = frustum.planes
    planes[0].setFromCoplanarPoints(vecTopLeft, vecFarTopLeft, vecFarTopRight)
    planes[1].setFromCoplanarPoints(vecTopRight, vecFarTopRight, vecFarDownRight)
    planes[2].setFromCoplanarPoints(vecFarDownRight, vecFarDownLeft, vecDownLeft)
    planes[3].setFromCoplanarPoints(vecFarDownLeft, vecFarTopLeft, vecTopLeft)
    planes[4].setFromCoplanarPoints(vecTopRight, vecDownRight, vecDownLeft)
    planes[5].setFromCoplanarPoints(vecFarDownRight, vecFarTopRight, vecFarTopLeft)
    planes[5].normal.multiplyScalar(-1)
  }

  // Build the broad-phase cache once per drag and reuse it across samples. When
  // no cache is passed (e.g. tests / one-off calls) a throwaway cache is built.
  const activeCache = cache ?? createMarqueeSelectionCache()
  if (!activeCache.built) {
    buildCache(activeCache, scene, camera, globalClipPlanes)
  }

  const { objects, ids } = evaluateCache(activeCache, frustum, camera, mode, selectionBounds2D)

  return { objectsInFrustum: objects, idsInFrustum: Array.from(ids) }
}
